import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { EMPTY, Observable, throwError } from "rxjs";

import { ErrorClass, PreconditionError, ResponseError } from "bp-common-models-lib";

import { ApiErrorBusService } from "@core-services/api-error-bus.service";
import {
	BadRequestError,
	DataAlreadyExistsError,
	ForbiddenError,
	GoneError,
	InternalServerError,
	InviteTx,
	LegalRightsRevokedError,
	LimitError,
	NotFoundError,
	OfflineError,
	PasswordValidationError,
	PossibleInternalTx,
	RequestTimeout,
	UnauthorizedError,
	ValidationError,
	VerificationError
} from "@models";
import { OfflineModalComponent } from "@modules/main/components/offline-modal/offline-modal.component";
import { ModalService } from "@core-services/modal.service";

interface GqlError {
	extensions: {
		bitpanda: {
			error: GqlResponseError;
			// TODO: eslint fix
			/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
			meta?: any;
		};
	};
}

interface GqlResponseError {
	httpCode: string;
	key: string;
	localizedDisplayMessage: string;
}

const apiToDomainErrorMap = {
	0: OfflineError,
	400: BadRequestError,
	401: UnauthorizedError,
	403: ForbiddenError,
	404: NotFoundError,
	406: LegalRightsRevokedError,
	408: RequestTimeout,
	409: DataAlreadyExistsError,
	410: GoneError,
	412: PreconditionError,
	419: LimitError,
	421: DataAlreadyExistsError,
	422: ValidationError,
	423: PasswordValidationError,
	429: LimitError,
	431: VerificationError,
	439: PossibleInternalTx,
	443: InviteTx,
	500: InternalServerError,
	503: InternalServerError
};

/**
 * This class takes care of mapping API errors to domain errors. It also takes care of common global side effects of
 * the thrown errors. If for instance there is an OfflineError thrown, this class will re-route the user to the offline route
 * to show the appropriate message.
 * NOTE: Side effect of the UnauthorizedError can't be handled in this class because they are not common on each endpoint.
 * Meaning that we need to ignore the UnauthorizedError in some cases and not logout the user. Thus, this side effects need
 * to be handled on lower layers of the app.
 */
@Injectable({
	providedIn: "root"
})
export class ExceptionsService {
	public constructor(
		private readonly errorBus: ApiErrorBusService,
		private readonly modalService: ModalService
	) {}

	public catchRestErrorAndRethrow(error: HttpErrorResponse): Observable<never> {
		const domainError = this.getDomainErrorFromRestApiError(error);
		return this.handleDomainErrorAndRethrow(domainError);
	}

	public catchGqlErrorAndRethrow({ error }: HttpErrorResponse): Observable<never> {
		// All network errors returned from the API have the same HTTP status --> 400. So we can't use
		// The status from the HttpResponseError, but we need rather to extract it from the GQL specific fields.

		const domainError = this.getDomainErrorFromGqlApiError(error.errors);
		return this.handleDomainErrorAndRethrow(domainError);
	}

	/**
	 * This method is intended to be used in the lower levels of the app inside the catchError() RxJS operator.
	 * Apollo throws error object that can consist of two types of errors --> EITHER networkError OR graphQLError.
	 * NetworkError is a proper HTTP error (not returned with the HTTP status 200), thus it will be caught by the
	 * error-interceptor.service and will be properly mapped to our domain error and rethrown. Meaning that, when it
	 * reaches this point, it is already properly mapped, and we can just rethrow this error if present in the object.
	 *
	 * On the other hand, graphQLErrors are returned with the HTTP status 200, so it's not possible to catch them in the
	 * error-interceptor.service. In this case, this method will map the GQL error to our domain error and rethrow it.
	 *
	 * @param  networkError - already properly mapped domain error --> ErrorClass | Error
	 * @param graphQLErrors - business error (e.g. insufficient funds) returned with the HTTP status 200 that will be
	 * mapped to a domain error <within this method
	 *
	 * @returns - observable with the domain error
	 */
	public extractGqlErrorAndRethrow: ({ networkError, graphQLErrors }) => Observable<never> = ({
		networkError,
		graphQLErrors
	}) => {
		// All network errors are already mapped to domain errors (error-interceptor.service caught them already and passed them
		// To this service for mapping), so we can just rethrow them
		if (networkError) {
			return throwError(() => networkError);
		}

		// GraphQLErrors on the other hand are returned with the HTTP status 200, so they need to be mapped to domain errors
		const errors = this.getDomainErrorFromGqlApiError(graphQLErrors);
		return throwError(() => errors);
	};

	private handleDomainErrorAndRethrow(domainError: ErrorClass | Error): Observable<never> {
		if (domainError instanceof ErrorClass && this.isAAError(domainError)) {
			return this.handleAAErrorAndSideEffects();
		}

		if (domainError instanceof ErrorClass && this.isAML5Error(domainError)) {
			return this.handleAML5ErrorAndSideEffects();
		}

		if (domainError instanceof OfflineError) {
			this.handleOfflineErrorSideEffects();
		}

		if (domainError instanceof ErrorClass) {
			this.handleDomainErrorSideEffects(domainError);
		}

		return throwError(domainError);
	}

	private getDomainErrorFromRestApiError(errorResponse: HttpErrorResponse): ErrorClass | Error {
		const { status, statusText, error } = errorResponse;
		const errorMessage = statusText || "couldn't call for url!" + JSON.stringify(errorResponse);
		const responseErrors = error?.errors ?? [];

		return this.getDomainErrorClass(status, errorMessage, responseErrors);
	}

	private getDomainErrorFromGqlApiError(errors: GqlError[]): ErrorClass | Error {
		const responseError = this.getResponseErrorFromGqlResponseError(errors);
		const { status: statusStr, title } = responseError;
		const status = parseInt(statusStr);
		return this.getDomainErrorClass(status, title, [responseError]);
	}

	private getDomainErrorClass(
		status: number,
		errorMessage: string,
		responseErrors: ResponseError[]
	): ErrorClass | Error {
		const domainErrorClass = apiToDomainErrorMap[status];

		return domainErrorClass ? new domainErrorClass(errorMessage, responseErrors) : new Error(errorMessage);
	}

	private getResponseErrorFromGqlResponseError(errors: GqlError[]): ResponseError {
		const [e] = errors;
		const { error, meta } = e.extensions.bitpanda;
		const { httpCode: status, localizedDisplayMessage: title, key: code } = error;
		return { status, code, title, ...(meta && { meta }) };
	}

	private isAML5Error(error: ErrorClass): boolean {
		return error instanceof PreconditionError && error.containsCode("aml.answers.required");
	}

	private isAAError(error: ErrorClass): boolean {
		return error instanceof PreconditionError && error.containsCode("questionnaire.onboarding-set.required");
	}

	private handleAML5ErrorAndSideEffects(): Observable<never> {
		this.errorBus.broadcastAML5Error();
		// To keep backwards compatibility where we didn't throw errors we just don't rethrow
		return EMPTY;
	}

	private handleAAErrorAndSideEffects(): Observable<never> {
		this.errorBus.broadcastAAError();
		// To keep backwards compatibility where we didn't throw errors we just don't rethrow
		return EMPTY;
	}

	private handleOfflineErrorSideEffects(): void {
		this.modalService.create(OfflineModalComponent).present();
	}

	private handleDomainErrorSideEffects(domainError: ErrorClass): void {
		this.errorBus.broadcast(domainError);
	}
}
